/**
 * The two call masking settings: a job's own, or a technician's defaults for the jobs they're
 * assigned. Plan feature voip.call-masking, so callers render this only when it is allowed.
 *
 * Hiding needs a number technicians can use: without one the technician would see neither the
 * contact's number nor a company line to reach them, so the checkbox is disabled (the server forces
 * it off too). It stays enabled while checked, so an admin can always turn hiding off.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { apiService } from '@/lib/api';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { HelpTip } from './RoutingPanel';
import type { PhoneNumberDTO } from '@/types';

export type CallMaskingField = 'route' | 'hide';

const TEXT = {
  job: {
    route: 'Ring the technician first when the contact calls',
    routeTip: "When this job's contact calls a company number, the call is routed to the assigned technician first. If unchecked, the call is routed to the office.",
    hide: "Hide the contact's number from the technician",
    hideTip: "The technician sees only the last 4 digits of the customer's number. When checked, all calls and texts use a company number.",
  },
  technician: {
    route: 'Ring this technician first when their customers call',
    routeTip: 'When a customer with an open job assigned to this technician calls a company number, the call is routed to this technician first. If unchecked, the call is routed to the office.',
    hide: 'Hide contact numbers from this technician',
    hideTip: "The technician sees only the last 4 digits of the customer's number. When checked, all calls and texts use a company number.",
  },
} as const;

const NO_TECHNICIAN_NUMBER_HINT = 'First allow technicians to use a number in Settings → Phones.';

/** Whether the selected company has an active number technicians can use. Unknown (loading) = yes. */
function useHasTechnicianNumber(): boolean {
  const { selectedCompanyId } = useGlobalCompany();
  const { data } = useQuery<PhoneNumberDTO[]>({
    queryKey: ['phone-numbers', selectedCompanyId],
    queryFn: () => apiService.getPhoneNumbers(),
    enabled: !!selectedCompanyId,
  });
  if (!data) return true;
  return data.some((n) => n.techniciansCanUse && n.isActive && !n.releasedAt);
}

export function CallMaskingOptions({
  variant,
  idPrefix,
  route,
  hide,
  onChange,
  disabled = false,
  labelClassName = 'text-sm text-gray-700',
  withEnableAll = false,
}: {
  variant: 'job' | 'technician';
  /** Unique per screen, for the checkbox ids. */
  idPrefix: string;
  route: boolean;
  hide: boolean;
  onChange: (field: CallMaskingField, value: boolean) => void | Promise<void>;
  disabled?: boolean;
  labelClassName?: string;
  /**
   * Adds an "Enable Call Masking" checkbox that sets both options at once (each can still be set on
   * its own), with an arrow that shows the two options. They start hidden.
   */
  withEnableAll?: boolean;
}) {
  const text = TEXT[variant];
  const hasTechnicianNumber = useHasTechnicianNumber();
  const hideBlocked = !hide && !hasTechnicianNumber;
  const [expanded, setExpanded] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  const row = (field: CallMaskingField, checked: boolean, label: string, tip: string, rowDisabled: boolean) => (
    <div className="flex items-start gap-2">
      <input
        type="checkbox"
        id={`${idPrefix}_${field}`}
        checked={checked}
        disabled={rowDisabled}
        onChange={(e) => void onChange(field, e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      />
      <label
        htmlFor={`${idPrefix}_${field}`}
        className={`${labelClassName} select-none ${rowDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        {label}
      </label>
      <HelpTip text={tip} />
    </div>
  );

  const options = (
    <div className="space-y-2">
      {row('route', route, text.route, text.routeTip, disabled)}
      {row('hide', hide, text.hide, text.hideTip, disabled || hideBlocked)}
      {hideBlocked && <p className="pl-6 text-xs text-gray-500">{NO_TECHNICIAN_NUMBER_HINT}</p>}
    </div>
  );

  if (!withEnableAll) return options;

  // Hiding can't be turned on without a technician number, so "all on" then means routing alone.
  const settable = hideBlocked ? [route] : [route, hide];
  const allOn = settable.every(Boolean);
  const someOn = route || hide;
  const setAll = async (next: boolean) => {
    setSavingAll(true);
    try {
      if (route !== next) await onChange('route', next);
      if (hide !== next && !(next && hideBlocked)) await onChange('hide', next);
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`${idPrefix}_all`}
          checked={allOn}
          // Partly on: a dash instead of a tick.
          ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
          disabled={disabled || savingAll}
          onChange={() => void setAll(!allOn)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        />
        <label
          htmlFor={`${idPrefix}_all`}
          className={`text-sm font-semibold text-gray-900 select-none ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          Enable Call Masking
        </label>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`${idPrefix}_options`}
          aria-label={expanded ? 'Hide call masking options' : 'Show call masking options'}
          title={expanded ? 'Hide options' : 'Show options'}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        </button>
      </div>
      {expanded && (
        <div id={`${idPrefix}_options`} className="pl-6">
          {options}
        </div>
      )}
    </div>
  );
}
